import { useCallback, useEffect, useRef, useState } from "react";
import { shareMatchInviteText } from "../../../../config/brand";
import { cn } from "../../../../design/utils/cn";
import { formatSessionRoomCodeDisplay } from "../../../../utils/matchRoomCode";
import { prepareArenaInviteShare } from "../../../../services/arenaInvitationService";
import { useMatchRoomShare } from "../hooks/useMatchRoomShare";
import MatchJoinQrPanel from "./MatchJoinQrPanel";
import s from "../styles/lobbyCreatedInvite.module.css";

/**
 * Room invite UI — create flow and Your rooms (organizer).
 * Prefer `inviteUrl` (/join/:invitationId) when the arena is server-ready.
 */
export default function RoomInviteDetailsPanel({
  roomCode,
  roomLabel,
  playerAccessCode = "",
  playerAccessMissingHint = "",
  refereeCode = "",
  refereeCodeMissingHint = "",
  inviteUrl = "",
  inviteReady = true,
  invitePreparingHint = "Preparing invite link…",
  inviteError = "",
  title,
  lead,
  primaryAction,
  secondaryAction,
  secondaryButtonRef,
  trailingAction,
  embedded = false,
  className = "",
}) {
  const providedUrl = inviteReady ? String(inviteUrl || "").trim() : "";
  const shouldPrepareLink = !String(inviteUrl || "").trim() && inviteReady !== false && !inviteError;
  const [preparedUrl, setPreparedUrl] = useState("");
  const [prepareError, setPrepareError] = useState("");
  const [preparingLink, setPreparingLink] = useState(false);

  useEffect(() => {
    if (!shouldPrepareLink || !roomCode) {
      setPreparedUrl("");
      setPrepareError("");
      setPreparingLink(false);
      return undefined;
    }
    let cancelled = false;
    setPreparingLink(true);
    setPrepareError("");
    (async () => {
      const ready = await prepareArenaInviteShare({
        arenaId: roomCode,
        arenaName: roomLabel,
      });
      if (cancelled) return;
      setPreparingLink(false);
      if (ready?.ok && ready.inviteAbsUrl) {
        setPreparedUrl(ready.inviteAbsUrl);
        return;
      }
      setPreparedUrl("");
      setPrepareError(ready?.message || "Invite link not ready");
    })();
    return () => {
      cancelled = true;
    };
  }, [shouldPrepareLink, roomCode, roomLabel]);

  const joinUrl = providedUrl || preparedUrl;
  const effectiveError = inviteError || prepareError;
  const linkIsReady = Boolean(joinUrl) && !effectiveError && !preparingLink;
  const { shareUrl, share, downloadQr } = useMatchRoomShare(roomCode, {
    inviteUrl: joinUrl,
  });
  const [feedback, setFeedback] = useState("");
  const canvasRef = useRef(null);

  const codeDisplay = formatSessionRoomCodeDisplay(roomCode);
  const playerAccess = (playerAccessCode || "").trim();
  const roomIsOpen = !playerAccess && !playerAccessMissingHint;
  const refAccess = (refereeCode || "").trim();
  const showRefBlock = Boolean(refAccess || refereeCodeMissingHint);
  const linkEnabled = linkIsReady && Boolean(shareUrl) && shareUrl.includes("/join/");

  const notify = useCallback((msg) => {
    if (msg) setFeedback(msg);
  }, []);

  const copyText = useCallback(
    async (text, okMsg, failMsg) => {
      try {
        await navigator.clipboard.writeText(text);
        notify(okMsg);
      } catch {
        notify(failMsg);
      }
    },
    [notify]
  );

  const copyRoomCode = useCallback(
    () => copyText(codeDisplay, "Arena code copied", "Could not copy arena code"),
    [codeDisplay, copyText]
  );

  const copyPlayerAccess = useCallback(async () => {
    if (roomIsOpen) {
      notify("Open arena — leave Access code blank");
      return;
    }
    await copyText(playerAccess, "Player access copied", "Could not copy access code");
  }, [copyText, notify, playerAccess, roomIsOpen]);

  const copyRefAccess = useCallback(async () => {
    if (!refAccess) return;
    await copyText(refAccess, "Referee access copied", "Could not copy referee access");
  }, [copyText, notify, refAccess]);

  const shareDetails = [
    shareMatchInviteText(),
    `Arena: ${roomLabel || codeDisplay}`,
    `Code: ${codeDisplay}`,
    roomIsOpen ? "Player access: leave blank" : `Player access: ${playerAccess}`,
  ]
    .filter(Boolean)
    .join(" · ");
  const inviteBundle = [shareDetails, shareUrl ? `Join link: ${shareUrl}` : null]
    .filter(Boolean)
    .join("\n");

  const handleShareLink = useCallback(async () => {
    if (!linkEnabled) {
      notify(inviteError || invitePreparingHint);
      return;
    }
    const shared = await share({
      text: shareDetails,
      copyText: inviteBundle,
    });
    if (shared) {
      notify(shared);
      return;
    }
    await copyText(inviteBundle, "Link copied to clipboard", "Could not copy link");
  }, [
    copyText,
    inviteBundle,
    shareDetails,
    inviteError,
    invitePreparingHint,
    linkEnabled,
    notify,
    share,
  ]);

  const handleSaveQr = useCallback(() => {
    if (!linkEnabled || !canvasRef.current) {
      notify(inviteError || invitePreparingHint);
      return;
    }
    const msg = downloadQr(canvasRef.current);
    if (msg) notify(msg);
  }, [downloadQr, inviteError, invitePreparingHint, linkEnabled, notify]);

  if (!roomCode) return null;

  const heading = title ?? (roomLabel ? `Arena ready · ${roomLabel}` : "Arena ready");
  const intro =
    lead ??
    "Share the invite link so friends can join after sign-in — no access code needed on the link.";

  return (
    <div
      className={cn(s.inlinePanel, embedded && s.embedded, className)}
      aria-labelledby={embedded ? undefined : "room-invite-details-title"}
    >
      {!embedded ? (
        <h3 id="room-invite-details-title" className={s.inlineTitle}>
          {heading}
        </h3>
      ) : null}
      {!embedded && roomLabel ? <p className={s.roomNameLine}>{roomLabel}</p> : null}
      {!embedded ? <p className={s.lead}>{intro}</p> : null}
      {embedded ? <p className={s.leadEmbedded}>{intro}</p> : null}

      {!linkIsReady && !effectiveError ? (
        <p className={s.feedback} role="status" data-testid="invite-preparing">
          {invitePreparingHint}
        </p>
      ) : null}
      {effectiveError ? (
        <p className={s.feedback} role="alert" data-testid="invite-not-ready">
          {effectiveError}
        </p>
      ) : null}

      <div className={s.codeRow}>
        <div className={s.codeBlock}>
          <span className={s.codeLabel}>Arena code</span>
          <button type="button" className={s.codeValue} onClick={copyRoomCode} title="Copy arena code">
            {codeDisplay}
          </button>
        </div>
        <div className={s.codeBlock}>
          <span className={s.codeLabel}>Player access</span>
          {roomIsOpen ? (
            <p className={s.openRoomHint}>Blank on join</p>
          ) : playerAccess ? (
            <button
              type="button"
              className={s.accessValue}
              onClick={copyPlayerAccess}
              title="Copy player access code"
            >
              {playerAccess}
            </button>
          ) : (
            <p className={s.missingSecret}>{playerAccessMissingHint}</p>
          )}
        </div>
      </div>

      {showRefBlock ? (
        <div className={s.orgBlock}>
          <span className={s.codeLabel}>Referee access (organizer only)</span>
          {refAccess ? (
            <button type="button" className={s.accessValue} onClick={copyRefAccess}>
              {refAccess}
            </button>
          ) : (
            <p className={s.missingSecret}>{refereeCodeMissingHint}</p>
          )}
          <p className={s.orgHint}>Not included in QR or share link.</p>
        </div>
      ) : null}

      <div className={s.qrRow}>
        <div className={s.qrCol}>
          {linkEnabled ? (
            <MatchJoinQrPanel
              shareUrl={shareUrl}
              roomLabel={roomLabel}
              compact
              qrSize={120}
              canvasRef={canvasRef}
            />
          ) : (
            <p className={s.openRoomHint} data-testid="invite-qr-waiting">
              QR available when invite is ready
            </p>
          )}
        </div>
        <div className={s.shareCol}>
          <button
            type="button"
            className={s.secondaryBtn}
            onClick={handleShareLink}
            disabled={!linkEnabled}
            data-testid="invite-share-link"
          >
            Share link
          </button>
          <button
            type="button"
            className={s.secondaryBtn}
            onClick={handleSaveQr}
            disabled={!linkEnabled}
          >
            Save QR
          </button>
          {trailingAction ? (
            <button
              type="button"
              className={trailingAction.tone === "danger" ? s.dangerBtn : s.secondaryBtn}
              onClick={trailingAction.onClick}
              disabled={trailingAction.disabled}
              data-testid={trailingAction.testId}
            >
              {trailingAction.label}
            </button>
          ) : null}
        </div>
      </div>

      {feedback ? (
        <p className={s.feedback} role="status">
          {feedback}
        </p>
      ) : null}

      {primaryAction ? (
        <button type="button" className={s.primaryBtn} onClick={primaryAction.onClick}>
          {primaryAction.label}
        </button>
      ) : null}
      {secondaryAction ? (
        <button
          ref={secondaryButtonRef}
          type="button"
          className={s.dismissBtn}
          onClick={secondaryAction.onClick}
        >
          {secondaryAction.label}
        </button>
      ) : null}
    </div>
  );
}
